import { Tier } from 'app/shared/account/tier.model';
import { SessionCondition, SessionConditionGroup } from './session-condition-group.model';
import { AndOrOperator } from '../../campaign/widgets.model';

export interface Audience {
  id?: string;
  name?: string;
  isDefault?: boolean;
  filters?: Array<AudienceFilter>;
  sessionConditions?: Array<SessionCondition>;
  operator?: AndOrOperator;
  usage?: Array<AudienceUsage>;
  reach?: number;
  reachType?: AudienceReachType;
  siteId?: number;
  accountGroupId?: string;
  reachTypeString?: string;
  createdTime?: string | Date;
  updatedTime?: string | Date;
  // Publish date of the most recent campaign that targeted this audience. Undefined/null = no usage on record.
  lastUsed?: string | Date;
}

export enum AudienceReachType {
  Weekly = 'Weekly',
  Total = 'Total',
}

export interface AudienceFlyoutResponse {
  audience?: Audience;
  sessionConditionGroup?: SessionConditionGroup;
  isRemoved?: boolean;
  isCreated?: boolean;
  widgetId?: number;
  source?: AudienceSource;
}

export enum AudienceSource {
  AbTest = 'AbTest',
  Campaign = 'Campaign',
}

export interface AudienceStatistics {
  visitors?: number;
  visits?: number;
  visitorsToday?: number;
  visitsToday?: number;
}

export interface AudienceUsage {
  campaignId?: number;
  widgetId?: number;
  campaignName?: string;
  dynamicName?: string;
  campaignType?: string;
}

export interface AudienceFilter {
  id?: number;
  type?: AudienceFilterType;
  app?: AudienceFilterApp;
  operator?: AudienceFilterOperator;
  value?: any;
  // Upper bound used only when operator === Between. Lower bound stays in value.
  upperValue?: number;
  filterTranslation?: string;
  filterTranslations?: AudienceFilterTranslations;
  filterConfig?: AudienceFilterConfig;
  timeOperator?: AudienceFilterTimeOperator;
  timeValue?: string | AudienceFilterTimeValue;
  weatherProperties?: WeatherPropertiesModel;
  frequencyType?: string;
  frequencyValue?: number;
  purchaseProperties?: PurchasePropertiesModel;
  eventProperties?: EventPropertiesModel;
  widgetEventProperties?: WidgetEventPropertiesModel;
  dimensionProperties?: DimensionPropertiesModel;
  isNew?: boolean;
  disabledOption?: string;
}

export interface EventPropertiesModel {
  // Null/empty eventName means "all events" (total_events semantics).
  eventName?: string;
}

export interface WidgetEventPropertiesModel {
  widgetId?: number;
  widgetEventType?: string;
}

export interface DimensionPropertiesModel {
  // Composite "type:value" string (e.g. "category:shoes") matching DataPlatform DimensionCounts keys.
  dimension?: string;
}

export interface AudienceFilterTimeValue {
  days?: number;
  startDate?: string | Date;
  endDate?: string | Date;
  upperDays?: number;
}

export enum AudienceFilterTimeOperator {
  AnyTime = 'AnyTime',
  TimeWindow = 'TimeWindow',
  SinceDate = 'SinceDate',
  BetweenDates = 'BetweenDates',
  BetweenDaysAgo = 'BetweenDaysAgo',
}

export enum AudienceFilterFrequencyOperator {
  Exactly = 'Exactly',
  AtLeast = 'AtLeast',
}

export enum AudienceFilterSalesOperator {
  Exactly = 'Exactly',
  LessThan = 'LessThan',
  MoreThan = 'MoreThan',
  Between = 'Between',
  Any = 'Any',
}

export interface AudienceFilterTranslations {
  type?: string;
  app?: string;
  operator?: string;
  value?: string;
  timeframeOperator?: string;
  timeframeValue?: string;
}

export enum AudienceFilterType {
  Identified = 'Identified',
  LoggedIn = 'LoggedIn',
  Consent = 'Consent',
  Country = 'GeoIpCountry',
  Region = 'GeoIpRegion',
  LatestSession = 'LatestSession',
  Goals = 'Goals',
  AppList = 'AppList',
  AppSegment = 'AppSegment',
  AppLabels = 'AppLabels',
  Tags = 'Tags',
  InterestProfiles = 'InterestProfiles',
  AppTags = 'AppTags',
  NpsScore = 'NpsScore',
  CsatScore = 'CsatScore',
  Weather = 'Weather',
  Temperature = 'Temperature',
  Purchase = 'Purchase',
  // DataPlatform-page additions — backend names match these string values verbatim.
  EventCount = 'EventCount',
  WidgetEventCount = 'WidgetEventCount',
  SessionCount = 'SessionCount',
  FirstSeen = 'FirstSeen',
  Dimension = 'Dimension',
  DimensionAffinity = 'DimensionAffinity',
  EngagementScore = 'EngagementScore',
}

export enum AudienceFilterApp {
  Rule = 'Rule',
  Mailchimp = 'Mailchimp',
  Voyado = 'Voyado',
  Klaviyo = 'Klaviyo',
  Emarsys = 'Emarsys',
  Custobar = 'Custobar',
  MailerLite = 'MailerLite',
  Shopify = 'Shopify',
  Brevo = 'Brevo',
  Dotdigital = 'Dotdigital',
  Omnisend = 'Omnisend',
  CleverReach = 'CleverReach',
  Shopware = 'Shopware',
  Custom = 'Custom',
}

export enum AudienceFilterOperator {
  Equals = 'Equals', // string
  NotEquals = 'NotEquals', // string
  StartsWith = 'StartsWith', // string
  EndsWith = 'EndsWith', // string
  Contains = 'Contains', // string
  NotContains = 'NotContains', // string
  AnyOf = 'AnyOf', // array
  NotAnyOf = 'NotAnyOf', // array
  Exists = 'Exists', // bool
  GreaterThan = 'GreaterThan', // number
  LessThan = 'LessThan', // number,
  // Numeric range ops (used by EventCount/SessionCount/Dimension/etc).
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThanOrEqual = 'LessThanOrEqual',
  Between = 'Between',
  MoreThan = 'MoreThan',
  PartOf = 'PartOf',
  NotPartOf = 'NotPartOf',
  HaveTag = 'HaveTag',
  NotHaveTag = 'NotHaveTag',
  DoNotHave = 'DoNotHave',
  Have = 'Have',
  HaveNot = 'HaveNot',
}

export interface AudienceFilterConfigGroup {
  titleKey?: string;
  filterConfigs?: Array<AudienceFilterConfig>;
}

export interface ExampleAudienceConfig {
  titleKey?: string;
  filters?: Array<AudienceFilter>;
  sessionConditions?: Array<SessionCondition>;
  operator?: AndOrOperator;
  chooseItemKey?: string;
  icon?: string;
  iconUrl?: string;
  descriptionKey?: string;
  isPopular?: boolean;
  app?: string;
  /** Login state is read live from the visit, so these examples are useless until
   *  the account has logged-in detection configured. */
  requiresLoggedInDetection?: boolean;
}

export interface AudienceFilterConfig {
  type?: AudienceFilterType;
  app?: AudienceFilterApp;
  titleKey?: string;
  titleOverride?: string;
  icon?: string;
  iconUrl?: string;
  defaultOperator?: AudienceFilterOperator;
  defaultValue?: string | Array<string> | boolean;
  descriptionKey?: string;
  subDescriptionKey?: string;
  supportedOperators?: Array<AudienceFilterOperator>;
  disabled?: boolean;
  tooltipTextKey?: string;
  limit?: number;
  defaultFrequencyType?: string;
  defaultFrequencyValue?: number;
  defaultSalesType?: string;
  isNew?: boolean;
  hide?: boolean;
  disabledOnTiers?: Tier[];
  skipValueSelection?: boolean;
}

export interface AudienceFilterOperatorConfig {
  operator?: AudienceFilterOperator;
  titleKey?: string;
  inputType?: AudienceFilterInputType;
}

export enum AudienceFilterInputType {
  TextField = 'TextField',
  TextArea = 'TextArea',
  BooleanDropdown = 'BooleanDropdown',
  MultiChoiceDropdown = 'MultiChoiceDropdown',
  SingleChoiceDropdown = 'SingleChoiceDropdown',
  JavascriptTextArea = 'JavascriptTextArea',
  Number = 'Number',
  Weather = 'Weather',
  Temperature = 'Temperature',
}

export interface AudienceFilterAppSetting {
  id: string;
  name: string;
}

export interface IdentifiedRateStat {
  identifiedPercentage?: number;
  date?: string;
  identified?: number;
  unidentified?: number;
  loggedIn?: number;
  /** Identified + anonymous, set only where the chart draws the total as a filled area. */
  total?: number;
}

export interface LiveData {
  action_title: string;
  action_type: string;
  action_url: string;
  country_code?: string;
  geolocation?: string;
  organization?: string;
  session_id?: number;
  time: number;
  uid?: number;
}

export interface WeatherPropertiesModel {
  weatherType?: WeatherTypeModel;
  preferedApp?: string;
  specificDay?: string | Date;
  daysForward?: number;
  checkNextWeekend: boolean;
  checkToday: boolean;
  checkTomorrow: boolean;
  inDays?: number;
  minTemperature?: number;
  maxTemperature?: number;
  customLongitude?: number;
  customLatitude?: number;
  minWindspeed?: number;
  maxWindspeed?: number;
}

export interface PurchasePropertiesModel {
  operator: string;
  value: number;
  upperValue: number;
  appliedCoupon: boolean;
}

export enum WeatherTypeModel {
  Sunny = 'Sunny',
  Cloudy = 'Cloudy',
  Rainy = 'Rainy',
  Snowy = 'Snowy',
  Temperature = 'Temperature',
}

export enum TimeFrame {
  SpecificDay = 'specificDay',
  DaysForward = 'daysForward',
  CheckNextWeekend = 'checkNextWeekend',
  Now = 'now',
  CheckToday = 'checkToday',
  CheckTomorrow = 'checkTomorrow',
  InDays = 'inDays',
}

export interface WeatherAppsKeyValuePair {
  id?: string;
  name?: string;
}
